import React, { useState, useCallback } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'bootstrap-icons/font/bootstrap-icons.css';

const validationSchema = Yup.object().shape({
  old_password: Yup.string().required('Current password is required'),
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .max(20, 'Password must be at most 20 characters')
    .required('New password is required'),
  password_confirmation: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Confirm your new password'),
});

const PROVIDER_LABELS = {
  google: 'Google',
  facebook: 'Facebook',
  twitter: 'Twitter',
};

function formatAuthProvidersMessage(authProviders) {
  if (!authProviders || authProviders.length === 0) return null;

  const labels = authProviders.map((p) => PROVIDER_LABELS[p] || p);
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

const PasswordField = ({
  id,
  name,
  label,
  value,
  onChange,
  onBlur,
  touched,
  error,
  autoComplete,
  show,
  onToggleShow,
}) => (
  <div className="form-group">
    <label htmlFor={id}>{label}</label>
    <div className="position-relative">
      <input
        id={id}
        type={show ? 'text' : 'password'}
        name={name}
        className="form-control pe-5"
        autoComplete={autoComplete}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
      />
      <button
        type="button"
        className="change-pw-eye btn btn-link position-absolute end-0 top-0 bottom-0 d-flex align-items-center py-0 px-2 border-0 text-secondary text-decoration-none"
        onClick={onToggleShow}
        tabIndex={-1}
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        <i className={`bi ${show ? 'bi-eye-slash' : 'bi-eye'}`} style={{ fontSize: '1.1rem' }} />
      </button>
    </div>
    {touched && error && <small className="text-danger">{error}</small>}
  </div>
);

/**
 * Logged-in password change (not forgot-password flow).
 * @param {string} apiPrefix - "user" | "seller" | "admin"
 * @param {string} userId - Mongo id from auth
 * @param {string} token - Bearer token
 * @param {boolean} hasPassword - false for social-only accounts
 * @param {string[]} authProviders - e.g. ["google", "facebook"]
 */
const ChangePasswordSection = ({
  apiPrefix,
  userId,
  token,
  hasPassword = true,
  authProviders = [],
}) => {
  const [open, setOpen] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [sendingSetupEmail, setSendingSetupEmail] = useState(false);

  const handleClose = useCallback(() => {
    setOpen(false);
    setShowOld(false);
    setShowNew(false);
    setShowConfirm(false);
    setFormKey((k) => k + 1);
  }, []);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleRequestPasswordSetup = useCallback(async () => {
    setSendingSetupEmail(true);
    try {
      const res = await axios.post(
        `${apiPrefix}/request-password-setup/${userId}`,
        {},
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json;charset=UTF-8',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (res.data.status) {
        toast.success(res.data.message || 'Verification link sent successfully', { autoClose: 4000 });
      } else {
        toast.error(res.data.message || 'Request failed', { autoClose: 4000 });
      }
    } catch (e) {
      const msg =
        e.response?.data?.message ||
        (typeof e.response?.data === 'string' ? e.response.data : null) ||
        e.message ||
        'Request failed';
      toast.error(msg, { autoClose: 4000 });
    } finally {
      setSendingSetupEmail(false);
    }
  }, [apiPrefix, userId, token]);

  if (!userId || !token) {
    return null;
  }

  const providersLabel = formatAuthProvidersMessage(authProviders);

  if (!hasPassword) {
    return (
      <div className="cart_wrap change-password-section">
        <div className="items_incart d-flex justify-content-between align-items-center flex-wrap gap-2">
          <span className="text-uppercase">Password</span>
        </div>
        <div className="profile_edit_wrap px-3 pb-4 pt-2">
          <p className="text-muted small mb-3">
            {providersLabel
              ? `Your account is secured with ${providersLabel}. You can set a password to also sign in with email and password.`
              : 'You can set a password to sign in with email and password.'}
          </p>
          <button
            type="button"
            className="btn custom_btn btn_yellow_bordered"
            disabled={sendingSetupEmail}
            onClick={handleRequestPasswordSetup}
          >
            {sendingSetupEmail ? 'Sending…' : 'Set new password'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cart_wrap change-password-section">
      <style>{`
        .change-password-section button.change-pw-open-btn,
        .change-password-section button.change-pw-open-btn *,
        .change-password-section button.change-pw-open-btn:hover,
        .change-password-section button.change-pw-open-btn:hover *,
        .change-password-section button.change-pw-open-btn:focus,
        .change-password-section button.change-pw-open-btn:focus *,
        .change-password-section button.change-pw-open-btn:active,
        .change-password-section button.change-pw-open-btn:active * {
          text-decoration: none !important;
          text-underline-offset: 0 !important;
        }
        .change-password-section button.change-pw-open-btn {
          color: #6c757d !important;
        }
        .change-password-section button.change-pw-open-btn:hover {
          color: #fff !important;
        }
        .change-password-section button.change-pw-open-btn:focus-visible {
          box-shadow: 0 0 0 0.2rem rgba(108, 117, 125, 0.35) !important;
        }
        .change-password-section .change-pw-eye.btn-link:hover,
        .change-password-section .change-pw-eye.btn-link:focus {
          text-decoration: none !important;
          color: #6c757d !important;
        }
        .change-password-section .in_form.change-password-form .form-group {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          margin-bottom: 1.25rem;
        }
        .change-password-section .in_form.change-password-form .form-group label {
          display: block;
          width: auto;
          margin-right: 0;
          margin-bottom: 0.4rem;
          font-weight: 600;
        }
        .change-password-section .in_form.change-password-form .form-group label::after {
          display: none;
        }
        .change-password-section .in_form.change-password-form .form-group .position-relative {
          width: 100%;
          max-width: 330px;
        }
        .change-password-section .in_form.change-password-form .form-control {
          width: 100%;
          max-width: 330px;
        }
        .change-password-section .in_form.change-password-form .form-group small.text-danger {
          display: block;
          width: 100%;
          max-width: 330px;
          margin-top: 0.35rem;
          line-height: 1.35;
          font-size: 0.875rem;
        }
        .change-password-section .in_form.change-password-form .change-password-form__actions {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.5rem;
        }
      `}</style>
      <div className="items_incart d-flex justify-content-between align-items-center flex-wrap gap-2">
        <span className="text-uppercase">Password</span>
        {!open && (
          <button
            type="button"
            className="change-pw-open-btn btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-2 text-decoration-none"
            onClick={handleOpen}
          >
            <i className="bi bi-pencil" aria-hidden />
            <span>Update password</span>
          </button>
        )}
      </div>

      {open && (
        <div className="profile_edit_wrap px-3 pb-4 pt-2">
          <p className="text-muted small mb-3">
            Enter your current password, then choose a new password (6–20 characters).
          </p>
          <Formik
            key={formKey}
            initialValues={{
              old_password: '',
              password: '',
              password_confirmation: '',
            }}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
              try {
                const res = await axios.put(
                  `${apiPrefix}/change-password/${userId}`,
                  {
                    old_password: values.old_password,
                    password: values.password,
                    password_confirmation: values.password_confirmation,
                  },
                  {
                    headers: {
                      Accept: 'application/json',
                      'Content-Type': 'application/json;charset=UTF-8',
                      Authorization: `Bearer ${token}`,
                    },
                  }
                );
                if (res.data.status) {
                  toast.success(res.data.message || 'Password updated successfully', { autoClose: 3000 });
                  resetForm();
                  handleClose();
                } else {
                  toast.error(res.data.message || 'Update failed', { autoClose: 4000 });
                }
              } catch (e) {
                const msg =
                  e.response?.data?.message ||
                  (typeof e.response?.data === 'string' ? e.response.data : null) ||
                  e.message ||
                  'Request failed';
                toast.error(msg, { autoClose: 4000 });
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <form className="in_form change-password-form" onSubmit={handleSubmit} autoComplete="off">
                <div className="form-row row">
                  <div className="col-md-12">
                    <PasswordField
                      id="cp-old_password"
                      name="old_password"
                      label="Current password"
                      value={values.old_password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched.old_password}
                      error={errors.old_password}
                      autoComplete="current-password"
                      show={showOld}
                      onToggleShow={() => setShowOld((v) => !v)}
                    />
                  </div>
                </div>
                <div className="form-row row">
                  <div className="col-12">
                    <PasswordField
                      id="cp-password"
                      name="password"
                      label="New password"
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched.password}
                      error={errors.password}
                      autoComplete="new-password"
                      show={showNew}
                      onToggleShow={() => setShowNew((v) => !v)}
                    />
                  </div>
                </div>
                <div className="form-row row">
                  <div className="col-12">
                    <PasswordField
                      id="cp-password_confirmation"
                      name="password_confirmation"
                      label="Confirm new password"
                      value={values.password_confirmation}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched.password_confirmation}
                      error={errors.password_confirmation}
                      autoComplete="new-password"
                      show={showConfirm}
                      onToggleShow={() => setShowConfirm((v) => !v)}
                    />
                  </div>
                </div>
                <div className="change-password-form__actions mt-2 d-flex flex-wrap align-items-center gap-2">
                  <button
                    type="submit"
                    className="btn custom_btn btn_yellow_bordered"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Updating…' : 'Save new password'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary ms-2"
                    disabled={isSubmitting}
                    onClick={handleClose}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </Formik>
        </div>
      )}
    </div>
  );
};

export default ChangePasswordSection;
